//Built-in libraries

import { MapPinIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
//Third party libraries
import { useLocation, useNavigate } from 'react-router';
//Custom components
import ProjectCard from '@/components/Sherpa/Projects/Project/ProjectCard';
import LoadingProjectsThumb from '@/components/UI/loading/LoadingProjectsThumb';
import { PageFooterPagination } from '@/components/UI/Pagination/PageFooterPagination';
import type { IGetProjectsServiceResData } from '../../../../../api/Sherpa/Project/modules/ProjectCore/types';
//Services
import { useProjectsData } from '../../../../../hooks/ReactQueryHooks/useProjectQuery';
import { usePermission } from '../../../../../hooks/usePermission';
import { workspaceSelector } from '../../../../../store';
import { notifyUser } from '../../../../../store/Toast';
//Assets and Miscellaneous
import type { FiltersInterface } from '../../../../../types/Miscellaneous';
import { isObjectEmpty } from '../../../../../utils';

//Component Begin
export const CardViewProjectsList = ({ filters }: { filters: FiltersInterface }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const { currentWorkspace } = useSelector(workspaceSelector);

  const [page, setPage] = useState<number>(1);
  const fetchProjects = useProjectsData({
    params: { perPage: 12, page, ...filters },
  });
  const projects = fetchProjects?.data?.data;
  const totalProjects = fetchProjects?.data?.meta?.total;
  const totalPages = Math.ceil(Number(totalProjects) / 12);

  const onPageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setPage((prevState) => (prevState > 1 ? page - 1 : 1));
    } else if (direction === 'next') {
      setPage((prevState) => (prevState === totalPages ? totalPages : page + 1));
    }
  };

  const projectDetailViewHandler = (projectId: number) => {
    if (hasPermission(['READ_PROJECT'])) {
      navigate(`${location.pathname}/${projectId}`, { state: location });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'You do not have permission to read project details',
          toastType: 'info',
        })
      );
    }
  };

  return (
    <>
      {fetchProjects.isLoading && <LoadingProjectsThumb />}
      {!fetchProjects.isLoading && projects && totalProjects > 0 && (
        <>
          <div className='flex justify-between items-center my-5'>
            <p className='mb-2'>{totalProjects} Projects</p>
            {totalPages > 1 && (
              <div className='flex gap-2'>
                <div
                  className={`rounded cursor-pointer ${
                    page === 1 ? 'bg-neutralColor-light' : 'bg-primaryColor-normal hover:bg-primaryColor-medium'
                  }`}
                >
                  <ChevronLeftIcon onClick={() => onPageChangeHandler('prev')} className='text-white h-6 w-6 p-1' />
                </div>
                <div
                  className={`bg-primaryColor-normal rounded cursor-pointer ${
                    page === totalPages ? 'bg-neutralColor-light' : 'bg-primaryColor-normal hover:bg-primaryColor-medium'
                  }`}
                >
                  <ChevronRightIcon onClick={() => onPageChangeHandler('next')} className='text-white h-6 w-6 p-1' />
                </div>
              </div>
            )}
          </div>
          <section className='mb-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5'>
            {projects.map((p: IGetProjectsServiceResData) => (
              <div key={p.id} onClick={() => projectDetailViewHandler(p.id)}>
                <ProjectCard project={p} />
              </div>
            ))}
          </section>
          {totalPages > 1 && <PageFooterPagination totalPages={totalPages} page={page} setPage={setPage} />}
        </>
      )}
      {!fetchProjects.isLoading && !isObjectEmpty(filters) && totalProjects === 0 && (
        <section>
          <div className='w-full flex justify-center items-center text-center h-screen/2 bg-white p-4'>
            <div>
              <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                <MapPinIcon className='h-8 w-8' />
                <h1>{totalProjects} Projects</h1>
              </div>
              <p>
                There are {totalProjects} projects matching your criteria in{' '}
                {currentWorkspace ? <span className=' text-primaryColor-normal'>{currentWorkspace?.name}</span> : 'all'} program.
              </p>
            </div>
          </div>
        </section>
      )}
    </>
  );
};
